"use client";

import React, { useEffect, useMemo, useState } from "react";
import CustomSlider from "./ui/CustomSlider/CustomSlider";

import { sanitizePhoneInput, sanitizeTextInput, validateName, validatePhoneNumber } from "@/app/utilities/formValidation";
import Image from "next/image";
import { getAllUserReviews, submitReview } from "../app/utilities/apis";
import Notification from "./Notification";
import CustomInput from "./ui/CustomInput";
import CustomTextarea from "./ui/CustomTextarea";
import ImageWrapper from "./ui/ImageWrapper";
import Skeleton from "./ui/Skeletons";
import { useLeadConsent } from "@/utils/hooks/useLeadConsent";
import LeadConsent from "./ui/Lead/LeadConsent";

interface Review {
    _id: string;
    reviewer_name: string;
    review_title: string;
    feedback: string;
    rating: number;
    created_at: string;
    review_type?: string;
    brand?: { _id: string; name: string };
    model?: { _id: string; name: string };
}

export default function CustomerReviews({ brand_id, model_id, review_type, limit = 20, page = 1, showWriteReviewButton = false, bodytype = "", heading = "Customer Reviews", cardsPerView = 2, mobileCardsPerView = 1.3,prefetchedReviews = [], showModelName = false }: {
    brand_id?: string;
    model_id?: string;
    review_type?: string | string[];
    limit?: number;
    page?: number;
    showWriteReviewButton?: boolean
    bodytype?: string;
    heading?: string;
    cardsPerView?: number;
    mobileCardsPerView?: number;
    prefetchedReviews?: Review[];
    showModelName?: boolean;
}) {
    const [tab, setTab] = useState<"Latest" | "Helpful" | "Critical">("Latest");
    const [expandedReviews, setExpandedReviews] = useState<Record<string, boolean>>({});
    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rating, setRating] = useState(5);
    const [nameError, setNameError] = useState<string | null>(null);
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [titleError, setTitleError] = useState<string | null>(null);
    const [descriptionError, setDescriptionError] = useState<string | null>(null);
    const [reviewData, setReviewData] = useState({ reviewer_name: "", mobile_number: "" });
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // The review form collects a mobile number → DPDP consent, fetched only once the form is opened.
    const consent = useLeadConsent(showReviewForm);

    // Consolidated error state
    const [formErrors, setFormErrors] = useState({
        name: null as string | null,
        phone: null as string | null,
        title: null as string | null,
        description: null as string | null
    });

    useEffect(() => {
        if (showReviewForm) {
            document.body.classList.add("overlay-scroll-mobile");
        } else {
            document.body.classList.remove("overlay-scroll-mobile");
        }

        // cleanup (page change / unmount)
        return () => {
            document.body.classList.remove("overlay-scroll-mobile");
        };
    }, [showReviewForm]);

    useEffect(() => {
        // Skip fetching if prefetched reviews are provided
        if(prefetchedReviews && prefetchedReviews.length > 0) {
            setReviews(prefetchedReviews);
            setLoading(false)
            return; 
        }
        if (!showWriteReviewButton && showReviewForm) {
            setShowReviewForm(false);
        }
        setLoading(true);
        getAllUserReviews({ brand_id, model_id, review_type, limit, page, bodytype })
            .then((res) => {
                if (res && Array.isArray(res.reviews)) {
                    setReviews(res.reviews);
                    setError(null);
                } else {
                    setReviews([]);
                    setError("No reviews found.");
                }
            })
            .catch(() => setError("Failed to load reviews."))
            .finally(() => setLoading(false));
    }, [brand_id, model_id, review_type, limit, page, showWriteReviewButton, showReviewForm]);

    const latestReviews = useMemo(() => {
        return [...reviews]
            .filter(r => (r.review_type ? String(r.review_type).toLowerCase() === 'latest' : r.rating >= 4.5))
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [reviews]);

    const helpfulReviews = useMemo(() => {
        return [...reviews]
            .filter(r => (r.review_type ? String(r.review_type).toLowerCase() === 'helpful' : r.rating >= 4.5))
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [reviews]);

    const criticalReviews = useMemo(() => {
        return [...reviews]
            .filter(r => (r.review_type ? String(r.review_type).toLowerCase() === 'critical' : r.rating < 4.0))
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [reviews]);

    const filtered = useMemo(() => {
        switch (tab) {
            case "Latest":
                return [...reviews]
                    .filter(r => (r.review_type ? String(r.review_type).toLowerCase() === 'latest' : r.rating >= 4.5))
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            case "Helpful":
                return [...reviews]
                    .filter(r => (r.review_type ? String(r.review_type).toLowerCase() === 'helpful' : r.rating >= 4.5))
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            case "Critical":
                return [...reviews]
                    .filter(r => (r.review_type ? String(r.review_type).toLowerCase() === 'critical' : r.rating < 4.0))
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            default:
                return reviews;
        }
    }, [tab, reviews]);

    // No container resize handling needed here — CustomSlider handles measurements internally.

    const toggleExpand = (reviewId: string) => {
        setExpandedReviews(prev => ({ ...prev, [reviewId]: !prev[reviewId] }));
    };

    // Centralized validation function
    const validateField = (field: string, value: string): string | null => {
        switch (field) {
            case 'name':
                if (!value.trim()) return "Name is required";
                const basicNameValidation = validateName(value);
                if (basicNameValidation) return basicNameValidation;
                const nameSanitization = sanitizeTextInput(value);
                return nameSanitization.isValid ? null : nameSanitization.error;

            case 'phone':
                if (!value.trim()) return "Mobile number is required";
                if (value.trim().length < 10) return "Mobile number must be at least 10 characters";
                return validatePhoneNumber(value);

            case 'title':
                if (!value.trim()) return "Title is required";
                if (value.length < 5) return "Title must be at least 5 characters";
                const titleSanitization = sanitizeTextInput(value);
                return titleSanitization.isValid ? null : titleSanitization.error;

            case 'description':
                if (!value.trim()) return "Description is required";
                if (value.length < 20) return "Description must be at least 20 characters";
                if (value.length > 5000) return "Description must be at most 5000 characters";
                const descSanitization = sanitizeTextInput(value);
                return descSanitization.isValid ? null : descSanitization.error;
            default:
                return null;
        }
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^A-Za-z\s]/g, "");
        setReviewData(prev => ({ ...prev, reviewer_name: value }));
        setFormErrors(prev => ({ ...prev, name: validateField('name', value) }));
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = sanitizePhoneInput(e.target.value).slice(0, 10);
        setReviewData(prev => ({ ...prev, mobile_number: value }));
        setFormErrors(prev => ({ ...prev, phone: validateField('phone', value) }));
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setTitle(value);
        setFormErrors(prev => ({ ...prev, title: validateField('title', value) }));
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setDescription(value);
        setFormErrors(prev => ({ ...prev, description: validateField('description', value) }));
    };

    // Hide navigation when items fit in desktop view (2 or fewer items)

    // If any review is expanded, we don't want all slides to stretch to the same height.
    const hasExpanded = useMemo(() => Object.values(expandedReviews).some(Boolean), [expandedReviews]);

    const [notification, setNotification] = useState<{
        message: string;
        isError: boolean;
        visible: boolean;
    }>({
        message: '',
        isError: false,
        visible: false,
    });

    const showNotification = (message: string, isError = false) => {
        setNotification({ message, isError, visible: true });
        setTimeout(() => {
            setNotification((prev) => ({ ...prev, visible: false }));
        }, 3000);
    };

    const resetForm = () => {
        setReviewData({ reviewer_name: "", mobile_number: "" });
        setTitle("");
        setDescription("");
        setRating(5);
        setFormErrors({ name: null, phone: null, title: null, description: null });
    };

    const handleReviewSubmit = async () => {
        if (!model_id || !brand_id) {
            console.error("Model ID and Brand ID are required");
            return;
        }

        // Prevent multiple submissions, and submitting without consent
        if (isSubmitting || !consent.canSubmit) return;

        // Validate all fields
        const errors = {
            name: validateField('name', reviewData.reviewer_name),
            phone: validateField('phone', reviewData.mobile_number),
            title: validateField('title', title),
            description: validateField('description', description)
        };

        setFormErrors(errors);

        // Check if any validation failed
        if (Object.values(errors).some(error => error !== null)) {
            showNotification("Please fill all fields before submitting", true);
            return;
        }

        const reviewPayload = {
            ...reviewData,
            review_title: title,
            feedback: description,
            rating,
            model_id,
            brand_id
        };

        setIsSubmitting(true);

        try {
            const response = await submitReview(reviewPayload);
            if (!response) {
                showNotification("Something went wrong. Please try after some time", true);
            } else {
                showNotification("Review submitted successfully", false);
                setShowReviewForm(false);
                resetForm();
            }
        } catch (error) {
            console.error("Error submitting review:", error);
            showNotification("Failed to submit review. Please try again.", true);
        } finally {
            setIsSubmitting(false);
        }
    }

    // Counts per tab to decide visibility
    const tabCounts = useMemo(() => {
        const latest = latestReviews.length;
        const helpful = helpfulReviews.length;
        const critical = criticalReviews.length;
        return { Latest: latest, Helpful: helpful, Critical: critical } as const;
    }, [latestReviews, helpfulReviews, criticalReviews]);

    const availableTabs = (['Latest', 'Helpful', 'Critical'] as const).filter(t => tabCounts[t] > 0);

    // Ensure current tab is one of the available tabs
    useEffect(() => {
        if (!loading && availableTabs.length > 0 && !availableTabs.includes(tab)) {
            setTab(availableTabs[0]);
        }
    }, [availableTabs, loading]);

    let showReviewsSection = true;
    // If no reviews at all, render nothing (after hooks are registered)
    if (!loading && !error && reviews.length === 0) {
        showReviewsSection = false;
    }

    const averageRating = reviews.reduce((sum, r) => sum + r.rating, 0) / (reviews.length || 1);
    // Text preview logic: character-based truncation with Read More/Less toggle
    // Uses per-review expanded state stored in `expandedReviews`

    // Calculate selected reviews based on current tab
    const selected = tab === 'Latest' ? latestReviews : tab === 'Helpful' ? helpfulReviews : criticalReviews;
    const bottomPad = selected.length > 2
        ? "pb-[10px] md:pb-[58px]"
        : hasExpanded
            ? "pb-4"
            : "";

    // CustomerReviews always fetches client-side (BrandPage passes no
    // prefetchedReviews), and it never actually suspends — so the Suspense
    // boundary around it at the call site can't reserve space for it. Render
    // a same-shaped skeleton here instead of `null` while loading, so the
    // section doesn't pop in from zero height once the fetch resolves.
    if (loading) {
        return (
            <section className={bodytype ? "" : "mt-6"}>
                <div className="review-padding">
                    <div className="mb-4 md:mb-8">
                        <Skeleton height={28} width={220} className="mb-4" />
                        <div className="flex gap-8 border-b border-gray-200 mb-4">
                            <Skeleton height={20} width={60} />
                            <Skeleton height={20} width={60} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Array.from({ length: cardsPerView }).map((_, i) => (
                                <Skeleton key={i} height={140} className="rounded-[10px] md:rounded-[24px]" />
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (!reviews?.length) {
        return null;
    }

    return (
        <section id={reviews.length > 0 ? "customer-reviews" : ''} className={bodytype ? "" : "mt-6"}>
            <div className={showReviewsSection ? "review-padding" : ""}>
                <div className={`mb-4 md:mb-8 ${bottomPad}`.trim()}>
                    <h2 className="mb-2 text-[21px] md:text-2xl font-bold heading-p">{heading}</h2>

                    {showWriteReviewButton && (
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                {reviews.length > 0 ? (
                                    <div className="flex items-center gap-3">
                                        <Image src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}yellow-star.svg`} width={20} height={20} alt="Star" title="Star" />
                                        <div>
                                            <div className="text-sm font-bold">{averageRating.toFixed(1)} <span className="text-xs text-gray-500">/5</span></div>
                                            <div className="text-xs text-gray-500">Based on {reviews.length} reviews</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-sm italic">Be the first to review</div>
                                )}
                            </div>

                            <button className="px-4 py-2 text-[14px] lead-gradient text-white rounded-lg font-medium bg-transparent border-none cursor-pointer" onClick={() => setShowReviewForm(true)}>
                                Write a Review
                            </button>
                        </div>
                    )}

                    {showReviewForm && showWriteReviewButton && (
                        <div>
                            <div className="fixed inset-0 bg-black/40 z-[999] md:hidden" onClick={() => setShowReviewForm(false)} />
                            <div className="p-4 bg-white md:p-0 md:bg-transparent">
                                <div className="fixed bottom-0 left-0 right-0 z-[1000] md:static md:z-auto px-[14px] py-[22px] bg-[linear-gradient(180deg,_#DFFFF9_0%,_#FFFFFF_100%)] rounded-t-2xl md:rounded-lg max-h-[85vh] md:max-h-none overflow-y-auto">
                                    <div className="flex justify-between items-center mb-3 relative">
                                        <p className="font-roboto text-[20px] leading-[28px] md:text-[23px] font-bold mx-auto md:mx-0">Write a car Review</p>
                                        <button onClick={() => setShowReviewForm(false)} className="md:hidden text-xl text-gray-500 top-[0px]  right-[0px] absolute cursor-pointer">
                                            <ImageWrapper src={"/popup-close.svg"} alt="Close" title="Close" width={18} height={18} cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`} />
                                        </button>
                                    </div>

                                    <div className="block text-center md:flex md:items-center md:gap-4 mb-4">
                                        <p className="mb-3 md:mb-0">Select Star Rating</p>
                                        <div className="flex gap-2 justify-center md:justify-start">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Image
                                                    key={star}
                                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}${star <= rating ? "yellow-star.svg" : "silver-star.svg"
                                                        }`}
                                                    width={24}
                                                    height={24}
                                                    className="w-5 h-5 md:w-6 md:h-6 cursor-pointer"
                                                    onClick={() => setRating(star)}
                                                    alt={`Rate ${star} star${star > 1 ? 's' : ''}`}
                                                    title={`Rate ${star} star${star > 1 ? 's' : ''}`}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-data">
                                        <CustomInput placeholder="Enter Review Title" value={title} onChange={handleTitleChange} className="bg-gray-50 text-[#636975]" error={formErrors.title || undefined} />
                                        <CustomTextarea placeholder="Enter Detailed Description" value={description} onChange={handleDescriptionChange} className="bg-gray-50" error={formErrors.description || undefined} />

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-4">
                                            <CustomInput value={reviewData.reviewer_name} placeholder="Enter name" leftIcon={<ImageWrapper src={'/user.svg'} alt="Name" title="Name" width={18} height={18} cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`} />} onChange={handleNameChange} error={formErrors.name || undefined} className="bg-gray-50 text-[#636975]" />
                                            <CustomInput type="tel" value={reviewData.mobile_number} placeholder="Enter mobile number" maxLength={10} minLength={10} leftIcon={<ImageWrapper src={'/phone.svg'} alt="Phone" title="Phone" width={18} height={18} cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`} />} onChange={handlePhoneChange} error={formErrors.phone || undefined} className="bg-gray-50" />
                                        </div>

                                        <LeadConsent consent={consent} className="mt-2" />

                                        <button type="button" onClick={handleReviewSubmit} disabled={isSubmitting || !consent.canSubmit} data-testid="review-submit" className={`px-4 py-2 mt-4 lead-gradient text-white rounded-lg mx-auto block ${isSubmitting || !consent.canSubmit ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer bg-[#008C8B]'}`}>{isSubmitting ? 'Submitting...' : 'Submit Review'}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <Notification message={notification.message} isVisible={notification.visible} isError={notification.isError} />

                    {showReviewsSection && (
                        <>
                            <nav className="flex gap-8 text-sm font-medium border-b border-gray-200 mb-4">
                                {availableTabs.map((t) => (
                                    <button key={t} onClick={() => setTab(t)} className={`relative pb-2 transition-all duration-200 cursor-pointer ${tab === t ? "text-teal-600" : "text-gray-500 hover:text-gray-700"}`}>
                                        {t}
                                        {tab === t && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" aria-hidden="true" />}
                                    </button>
                                ))}
                            </nav>

                            {/* Render selected reviews */}
                            {(() => {
                                if (!selected || selected.length === 0) return <div className="py-6">No reviews found.</div>;
                                const showNavigation = selected.length > cardsPerView;

                                return (
                                    <CustomSlider key={tab}
                                        responsive={{ mobile: mobileCardsPerView, tablet: cardsPerView, desktop: cardsPerView }}
                                        itemsToShow={4}
                                        showArrows={showNavigation}
                                        gap={16}
                                        className={`${hasExpanded ? "items-start" : "items-stretch"}`.trim()}
                                    >
                                        {selected.map((r) => {
                                            const isExpanded = !!expandedReviews[r._id];
                                            const maxLength = 145;
                                            const isLong = r.feedback.length > maxLength;
                                            const displayDesc = isExpanded || !isLong ? r.feedback : r.feedback.slice(0, maxLength) + "...";
                                            const shouldShowToggle = isLong;
                                            const displayName = r.reviewer_name;
                                            const initials = r.reviewer_name?.split(" ").map((n: string) => n[0]).join("") || "U";
                                            const reviewDate = new Date(r.created_at);
                                            const formattedDate = `${reviewDate.toLocaleDateString('en-US', { month: 'short' })} ${reviewDate.getDate()}, ${reviewDate.getFullYear()}`;
                                            const brandModel = `${r.brand?.name || ''} ${r.model?.name || ''}`;

                                            return (
                                                <div key={r._id} className="h-full">
                                                    <div className="bg-white border border-gray-200 rounded-[10px] md:rounded-[24px] p-4 mr-1 h-full flex flex-col">
                                                        <div className="grid grid-cols-[40px_1fr] gap-2 items-center flex-1">
                                                            <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">{initials}</div>
                                                            <div>
                                                                 <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                                                    <div className="text-sm break-words">
                                                                        {displayName}
                                                                    </div>

                                                                    <div className="flex gap-[5px] items-center flex-wrap">
                                                                        <div className="text-xs text-[#6A7282]">
                                                                            On {formattedDate}
                                                                        </div>

                                                                        <div className="flex gap-[5px] items-center">
                                                                            <span className="text-xs text-[#000]">{r.rating}</span>

                                                                            <Image
                                                                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}yellow-star.svg`}
                                                                                width={10}
                                                                                height={10}
                                                                                alt="rating"
                                                                                title="rating"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {/* <div className="text-sm mt-1 flex items-center"> */}
                                                                    {/* {[1, 2, 3, 4, 5].map((star) => (
                                                                        <span key={star} className={star <= Math.round(r.rating) ? "text-yellow-400" : "text-gray-300"} aria-label={star <= Math.round(r.rating) ? "Filled star" : "Empty star"}>★</span>
                                                                    ))} */}
                                                                    {/* <span className="ml-1 text-xs text-gray-500">({r.rating})</span> */}
                                                                {/* </div> */}

                                                                {showModelName && (
                                                                    <span className="text-sm">For <b>{brandModel}</b></span>
                                                                )}
                                                            </div>
                                                            <div className="col-span-2 px-2">
                                                                <p className="text-[15px] text-[##101828] font-[500] mt-2 bold-font">{r.review_title}</p>
                                                                <p className="text-[13px] text-[#4A5565] leading-[21px] mt-1">
                                                                    {displayDesc.split('\n').map((line: string, idx: number) => (<React.Fragment key={idx}>{line}<br /></React.Fragment>))}
                                                                    {shouldShowToggle && (
                                                                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleExpand(r._id); }} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} className="inline text-[#155DFC] text-sm font-medium p-0 bg-transparent border-none cursor-pointer">{isExpanded ? 'Read Less' : 'Read More'}</button>
                                                                    )}
                                                                </p>
                                                                {/* <div className="text-xs text-gray-400 mt-2">On {new Date(r.created_at).toLocaleDateString()}</div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </CustomSlider>
                                );
                            })()}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}